import React, { useEffect, useRef, useState } from "react";
import { get } from "lodash";
import { useSelector, useDispatch } from "react-redux";

import ModalV2 from "@tvg/atomic-ui/_templates/ModalV2";
import RegistrationTvPreferences from "@tvg/atomic-ui/_templates/RegistrationTvPreferences";
import capiRequests from "@tvg/api/capi";
import {
  onAppliesProviderGtmEvent,
  onRemoveProviderGtmEvent,
  onCloseProviderModalGtmEvent
} from "../utils/gtm/tvgProvider";
import {
  handleTvPreferencesModalRegistrationAction,
  setTvPreferencesScreenRegistrationAction
} from "../actions";
import { handleSelectChangeGtm } from "../utils/handlers";
import {
  getIsTvPreferencesModalOpen,
  getRegistrationTexts,
  getStoredResidentialStateAbbr,
  getStoredSelectedProvider
} from "../utils/selectors";

export const closeModal = (dispatch) => {
  dispatch(handleTvPreferencesModalRegistrationAction(false));
  onCloseProviderModalGtmEvent();
};

export const handleProviderGtmClick = (fieldName, title) => {
  handleSelectChangeGtm(fieldName, false, title, true);
};

export const handleSubmitTvPreferences = ({
  dispatch,
  data,
  storedSelectedProvider,
  providersList,
  defaultProviders
}) => {
  const { tvProvider, selectProviderInput, ...mainProviders } = data;
  const selectedProvider = Object.keys(mainProviders).find(
    (provider) => mainProviders[provider]
  );
  const selectedProviderObj = tvProvider
    ? providersList.find((provider) => provider.value === selectProviderInput)
    : defaultProviders.find((provider) => provider.value === selectedProvider);

  if (
    selectedProviderObj &&
    selectedProviderObj.value !== storedSelectedProvider.value
  ) {
    dispatch(
      setTvPreferencesScreenRegistrationAction({
        value: get(selectedProviderObj, "value", ""),
        key: get(selectedProviderObj, "key", "")
      })
    );

    onAppliesProviderGtmEvent(get(selectedProviderObj, "key"), "TVProvider");
  } else if (!selectedProviderObj && storedSelectedProvider.value !== "") {
    dispatch(
      setTvPreferencesScreenRegistrationAction({
        value: "",
        key: ""
      })
    );

    onRemoveProviderGtmEvent("TVProvider");
  }

  dispatch(handleTvPreferencesModalRegistrationAction(false));
};

export const TvPreferencesModalComponent = ({ device }) => {
  const dispatch = useDispatch();

  const registrationTexts = useSelector(getRegistrationTexts);

  const isTvPreferencesModalOpen = useSelector(getIsTvPreferencesModalOpen);
  const storedResidentialStateAbbr = useSelector(getStoredResidentialStateAbbr);
  const storedSelectedProvider = useSelector(getStoredSelectedProvider);

  const formRef = useRef(null);
  const [providersList, setProvidersList] = useState([]);
  const [residentialStateAbbr, setResidentialStateAbbr] = useState("");
  const { defaultProviders } = registrationTexts.modals.tvPreferences;

  useEffect(() => {
    // get list of TV providers based on residential state
    const getTvProvidersByState = async () => {
      try {
        const providers = await capiRequests.fetchTvProvidersByState(
          storedResidentialStateAbbr
        );
        const defaultProvidersValues = defaultProviders.map(
          ({ value }) => value
        );
        const data = providers.data
          .map((provider) => ({
            value: provider.signalProviderId,
            key: provider.name
          }))
          .filter(
            (provider) => !defaultProvidersValues.includes(provider.value)
          );
        setProvidersList(data);
      } catch (err) {
        console.log(err);
      }
    };

    // clear TV provider from review screen if user changes residential state
    if (residentialStateAbbr !== storedResidentialStateAbbr) {
      dispatch(
        setTvPreferencesScreenRegistrationAction({
          value: "",
          key: ""
        })
      );

      getTvProvidersByState();
    }

    setResidentialStateAbbr(storedResidentialStateAbbr);
  }, [storedResidentialStateAbbr]);

  const isMobile = device === "mobile";

  return (
    <ModalV2
      title={registrationTexts.modals.tvPreferences.modalTitle}
      isOpen={isTvPreferencesModalOpen}
      onClose={() => closeModal(dispatch)}
      onOverlayClick={() => closeModal(dispatch)}
      titleType="info"
      qaLabel="modal-tv-preferences"
      animation={isMobile ? "bottom" : "bottomFloating"}
      offsetLeft={isMobile ? 0 : 12}
      offsetRight={isMobile ? 0 : 12}
      fixedWidth={isMobile ? "100%" : "375px"}
      isFullWidth={isMobile}
      offsetTop={66}
    >
      {() => (
        <RegistrationTvPreferences
          formRef={formRef}
          title={registrationTexts.modals.tvPreferences.title}
          description={registrationTexts.modals.tvPreferences.description}
          buttonText={registrationTexts.modals.tvPreferences.buttonText}
          selectDefaultValueText={
            registrationTexts.modals.tvPreferences.selectDefaultValue
          }
          defaultProviders={defaultProviders}
          handleProviderGtmClick={(fieldName) =>
            handleProviderGtmClick(
              fieldName,
              registrationTexts.modals.tvPreferences.modalTitle
            )
          }
          providersList={providersList}
          handleSubmit={(data) =>
            handleSubmitTvPreferences({
              dispatch,
              data,
              storedSelectedProvider,
              providersList,
              defaultProviders
            })
          }
          storedSelectedProvider={storedSelectedProvider}
        />
      )}
    </ModalV2>
  );
};

export default TvPreferencesModalComponent;
